import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../routes/routes';
import { ClippedImage } from './ClippedImage';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import logo from '../assets/logo-progressive.svg';
import { useFrameDimensions } from '../utils/clipPath';

const FooterWrapper = styled(motion.div)`
	width: 100%;
	overflow: hidden;
	position: relative;
`;

const TransitionOverlay = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100vh;
	background: white;
	z-index: 50;
`;

const FooterContainer = styled.footer`
	width: 100%;
	position: relative;
	display: grid;
	grid-template-areas: 'stack';
	place-items: center;
	z-index: 0;
	height: 100%;
`;

const Frame = styled.div`
	position: relative;
	height: 64svh;
	width: ${(props) => `min(100%, calc(64svh * ${props.$aspectRatio}))`};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	transform-origin: center center;
	grid-area: stack;

	> div {
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;

const Content = styled.div`
	grid-area: stack;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	z-index: 1;
`;

// Preload an image and return a promise that resolves when it's loaded
const preloadImage = (src) => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = src;
	});
};

// Preload a route's assets
const preloadRoute = async (route) => {
	if (!route?.masthead?.image) {
		return Promise.resolve();
	}

	try {
		await preloadImage(route.masthead.image);
	} catch (error) {
		console.error('Error preloading image:', error);
	}
};

const Footer = () => {
	const location = useLocation();
	const frameRef = useRef(null);
	const [isExpanding, setIsExpanding] = useState(false);
	const [showOverlay, setShowOverlay] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [overlayRoute, setOverlayRoute] = useState(null);
	const navigate = useNavigate();

	// Get routes and dimensions
	const navRoutes = routes.filter((route) => route.showInNav === true);
	const currentIndex = navRoutes.findIndex((route) => route.path === location.pathname);

	// Determine the next route
	let nextRoute;

	if (currentIndex === -1) {
		// Current route is not in navigation, find the next showInNav route by type
		const currentRoute = routes.find((route) => route.path === location.pathname);

		if (currentRoute) {
			const currentType = currentRoute.type;

			// Find the next route with showInNav: true that has a different type
			// First, get all the unique types in order of appearance
			const uniqueTypes = [];
			routes.forEach((route) => {
				if (route.type && !uniqueTypes.includes(route.type)) {
					uniqueTypes.push(route.type);
				}
			});

			// Find the index of the current type
			const currentTypeIndex = uniqueTypes.indexOf(currentType);

			// Find the next type in the sequence (or loop back to the first)
			const nextTypeIndex = currentTypeIndex !== -1 && currentTypeIndex < uniqueTypes.length - 1 ? currentTypeIndex + 1 : 0;
			const nextType = uniqueTypes[nextTypeIndex];

			// Find the first route with showInNav: true that has the next type
			nextRoute = routes.find((route) => route.showInNav === true && route.type === nextType);

			// If no matching route found, use the first showInNav route
			if (!nextRoute) {
				nextRoute = navRoutes[0];
			}
		} else {
			// Fallback to first showInNav route
			nextRoute = navRoutes[0];
		}
	} else {
		// Current route is in navigation, use next route or loop back to first
		nextRoute = navRoutes[currentIndex + 1] || navRoutes[0];
	}

	const aspectRatio = nextRoute.masthead?.dimensions ? nextRoute.masthead.dimensions.width / nextRoute.masthead.dimensions.height : 1.5;
	const dimensions = useFrameDimensions(frameRef, nextRoute.masthead?.dimensions);

	// Get dimensions for both original and overlay frames
	const overlayDimensions = overlayRoute?.masthead?.dimensions || nextRoute.masthead?.dimensions;
	const overlayAspectRatio = overlayDimensions ? overlayDimensions.width / overlayDimensions.height : 1.5;

	// Keep user pinned to bottom during expansion
	const handleAnimationUpdate = () => {
		if (isExpanding) {
			requestAnimationFrame(() => {
				window.scrollTo({
					top: document.documentElement.scrollHeight - window.innerHeight,
					behavior: 'auto',
				});
			});
		}
	};

	// Reset states on route change
	useEffect(() => {
		setIsExpanding(false);
		setShowOverlay(false);
		setIsTransitioning(false);
		setOverlayRoute(null);
	}, [location.pathname]);

	const handleClick = async () => {
		if (isTransitioning) return;
		setIsTransitioning(true);

		// Store current route for overlay
		setOverlayRoute(nextRoute);

		// 1. Expand the footer (reduced from 1500ms to 800ms)
		setIsExpanding(true);
		await new Promise((resolve) => setTimeout(resolve, 800));

		// 2. Show the overlay footer (reduced from 500ms to 300ms)
		setShowOverlay(true);
		await new Promise((resolve) => setTimeout(resolve, 300));

		// 3. Preload the next route's content
		try {
			await Promise.all([
				preloadRoute(nextRoute),
				// Minimum delay reduced from 2000ms to 1000ms
				new Promise((resolve) => setTimeout(resolve, 1000)),
			]);

			// 4. Scroll to top and navigate
			window.scrollTo(0, 0);
			navigate(nextRoute.path);
		} catch (error) {
			console.error('Error during transition:', error);
			setIsTransitioning(false);
			setIsExpanding(false);
			setShowOverlay(false);
		}
	};

	return (
		<>
			<FooterWrapper initial={{ height: '85svh' }} animate={{ height: isExpanding ? '100svh' : '85svh' }} transition={{ duration: 0.8, ease: 'easeInOut' }} style={{ cursor: isTransitioning ? 'default' : 'pointer' }} onClick={handleClick} onUpdate={handleAnimationUpdate} id="footer">
				<FooterContainer>
					<Frame ref={frameRef} $aspectRatio={aspectRatio}>
						{nextRoute.masthead && <ClippedImage src={nextRoute.masthead.image} alt={nextRoute.masthead.alt || `Preview of ${nextRoute.name}`} clipPaths={nextRoute.clipPaths} isExpanded={false} transitionDuration={0} dimensions={dimensions} />}
					</Frame>
					<Content>
						<div className="absolute top-0 right-0 left-0">
							<div className="container px-4">
								<div className="py-6">
									<div>
										<div className="flex flex-col gap-6 justify-between md:flex-row">
											<span className="text-[18px]">Up Next:</span>
											<span
												className="font-trade-compressed uppercase m-0 text-[3.125rem] font-bold tracking-widest leading-[1]"
												style={{
													borderBottom: `4px solid ${nextRoute.fullNavColor}`,
												}}
											>
												{nextRoute.name}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="absolute right-0 bottom-0 left-0">
							<div className="container">
								<div className="py-6">
									<div>
										<hr />
										<div className="flex justify-end items-end mt-6">
											<a href="https://www.progressive.com/" target="_blank" rel="noreferrer">
												<img src={logo} alt="Progressive" className="max-w-[163px]" />
											</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Content>
				</FooterContainer>
			</FooterWrapper>

			<AnimatePresence mode="wait">
				{showOverlay && overlayRoute && (
					<TransitionOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} style={{ backgroundColor: 'white' }}>
						<FooterContainer>
							<Frame $aspectRatio={overlayAspectRatio}>{overlayRoute.masthead && <ClippedImage src={overlayRoute.masthead.image} alt={overlayRoute.masthead.alt || `Preview of ${overlayRoute.name}`} clipPaths={overlayRoute.clipPaths} isExpanded={false} transitionDuration={0} dimensions={overlayDimensions} />}</Frame>
							<Content>
								<div className="absolute top-0 right-0 left-0">
									<div className="container px-4">
										<div className="py-6">
											<div>
												<div className="flex flex-col gap-6 justify-between md:flex-row">
													<span className="text-[18px]">Up Next:</span>
													<span
														className="font-trade-compressed uppercase m-0 text-[3.125rem] font-bold tracking-widest leading-[1]"
														style={{
															borderBottom: `4px solid ${overlayRoute.fullNavColor}`,
														}}
													>
														{overlayRoute.name}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="absolute right-0 bottom-0 left-0">
									<div className="container">
										<div className="py-6">
											<div>
												<hr />
												<div className="flex justify-end items-end mt-6">
													<a href="https://www.progressive.com/" target="_blank" rel="noreferrer">
														<img src={logo} alt="Progressive" className="max-w-[163px]" />
													</a>
												</div>
											</div>
										</div>
									</div>
								</div>
							</Content>
						</FooterContainer>
					</TransitionOverlay>
				)}
			</AnimatePresence>
		</>
	);
};

export default Footer;
